import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Card, CardBody, Row, Col, Input, Table, Spinner, Button,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
  getFleetAllotments, saveFleetAllotment, removeFleetAllotment, getVehicles, getCampList,
  getAllotmentProjects, getAllotmentProjectMembers,
} from "../../services/fleet"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

const num = v => (v == null ? "0" : Number(v).toLocaleString())
const today = () => new Date().toISOString().slice(0, 10)
const pctColor = p => (p == null ? "#64748b" : p > 100 ? "#dc2626" : p >= 70 ? "#16a34a" : "#d97706")
const blankSite = () => ({ site_name: "", project_id: "", area_project: "", qty: "", member_ids: [] })
const blankForm = () => ({
  id: null, date: today(), vehicle_id: "", driver_name: "", driver_type: "assigned",
  source: "", shift: "day",
  duty_start: "", duty_off: "", division: "", contact_person: "", remarks: "",
  sites: [blankSite()],
})

/** Operations → Fleet → Daily Allotment. Which vehicle runs from which camp to which sites, with
 *  seats carried vs capacity (utilisation). Mirrors the AMJ daily-transportation register. */
const FleetAllotment = () => {
  useCollapsedSidebar()
  document.title = "Daily Vehicle Allotment | Calyvern"
  const [date, setDate] = useState(today())
  const [data, setData] = useState({ rows: [], summary: {} })
  const [loading, setLoading] = useState(false)
  const [vehicles, setVehicles] = useState([])
  const [camps, setCamps] = useState([])
  const [projects, setProjects] = useState([])
  // members assignable to a site leg, cached per project+camp — the pool the boarding list is picked from
  const [memberPool, setMemberPool] = useState({})
  const requestedPools = useRef({})
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(blankForm())
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      const r = await getFleetAllotments({ date })
      setData(r?.data || { rows: [], summary: {} })
    } catch (e) { toastError(e?.message || "Failed to load allotments") } finally { setLoading(false) }
  }
  useEffect(() => { load() /* eslint-disable-next-line */ }, [date])
  useEffect(() => {
    ;(async () => {
      try { const r = await getVehicles({ status: 1 }); setVehicles(r?.data || []) } catch (e) { /* non-fatal */ }
      try { const c = await getCampList(); setCamps(c?.data || []) } catch (e) { /* non-fatal */ }
      try { const p = await getAllotmentProjects(); setProjects(p?.data || []) } catch (e) { /* non-fatal */ }
    })()
  }, [])

  // pull the member pool for every project leg on screen, per the camp the vehicle stages from
  useEffect(() => {
    const camp = form.source || ""
    form.sites.forEach(st => {
      if (!st.project_id) return
      const key = `${st.project_id}|${camp}`
      if (requestedPools.current[key]) return
      requestedPools.current[key] = true
      setMemberPool(p => ({ ...p, [key]: { loading: true, members: [] } }))
      getAllotmentProjectMembers({ project_id: st.project_id, camp })
        .then(r => setMemberPool(p => ({
          ...p,
          [key]: {
            loading: false,
            members: r?.data?.members || [],
            total_assigned: r?.data?.total_assigned || 0,
            camp_link_available: !!r?.data?.camp_link_available,
          },
        })))
        .catch(() => setMemberPool(p => ({ ...p, [key]: { loading: false, members: [], error: true } })))
    })
  }, [form.sites, form.source])

  // drivers assigned to vehicles in the master — the pool to pick from when the vehicle's own
  // driver stands in; a "hired" driver is anyone outside this pool (typed free-hand).
  const assignedDrivers = useMemo(
    () => [...new Set(vehicles.map(v => (v.driver_name || "").trim()).filter(Boolean))].sort(),
    [vehicles]
  )

  const rows = data.rows || []
  const s = data.summary || {}

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const setSite = (i, k, v) => setForm(f => ({ ...f, sites: f.sites.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }))
  const addSite = () => setForm(f => ({ ...f, sites: [...f.sites, blankSite()] }))
  const rmSite = i => setForm(f => ({ ...f, sites: f.sites.filter((_, j) => j !== i) }))

  // picking a project names the leg and resets whoever was boarding the previous one
  const pickProject = (i, projectId) => {
    const p = projects.find(x => String(x.id) === String(projectId))
    setSiteFields(i, {
      project_id: projectId ? String(projectId) : "",
      site_name: p ? p.name : "",
      area_project: p?.location || "",
      member_ids: [], qty: "",
    })
  }
  const setSiteFields = (i, patch) =>
    setForm(f => ({ ...f, sites: f.sites.map((r, j) => (j === i ? { ...r, ...patch } : r)) }))

  // seats carried for a leg follow the boarding list, so the two can never disagree
  const toggleMember = (i, empId) => setForm(f => ({
    ...f,
    sites: f.sites.map((r, j) => {
      if (j !== i) return r
      const has = r.member_ids.some(x => String(x) === String(empId))
      const member_ids = has
        ? r.member_ids.filter(x => String(x) !== String(empId))
        : [...r.member_ids, empId]
      return { ...r, member_ids, qty: member_ids.length }
    }),
  }))
  const toggleAllMembers = (i, members) => setForm(f => ({
    ...f,
    sites: f.sites.map((r, j) => {
      if (j !== i) return r
      const all = r.member_ids.length === members.length
      const member_ids = all ? [] : members.map(m => m.id)
      return { ...r, member_ids, qty: member_ids.length }
    }),
  }))

  const openAdd = () => { setForm({ ...blankForm(), date }); setModal(true) }
  const openEdit = r => {
    setForm({
      id: r.id, date: r.date, vehicle_id: String(r.vehicle_id), driver_name: r.driver_name || "",
      driver_type: r.driver_type === "hired" ? "hired" : "assigned",
      source: r.source || "", shift: r.shift || "day", duty_start: r.duty_start || "", duty_off: r.duty_off || "",
      division: r.division || "", contact_person: r.contact_person || "", remarks: r.remarks || "",
      sites: (r.sites?.length ? r.sites : [blankSite()]).map(x => ({
        site_name: x.site_name || "", project_id: x.project_id ? String(x.project_id) : "",
        area_project: x.area_project || "", qty: x.qty ?? "", member_ids: x.member_ids || [],
      })),
    })
    setModal(true)
  }

  // when a vehicle is picked, default to its own assigned driver from the master
  const pickVehicle = id => {
    const v = vehicles.find(x => String(x.id) === String(id))
    setForm(f => ({
      ...f, vehicle_id: id,
      driver_name: f.driver_type === "hired" ? f.driver_name : (v?.driver_name || f.driver_name || ""),
    }))
  }

  // the picked vehicle — its seater is the capacity shown against the selection
  const selectedVehicle = useMemo(
    () => vehicles.find(x => String(x.id) === String(form.vehicle_id)) || null,
    [vehicles, form.vehicle_id]
  )

  // switch between the vehicle's own driver and a hired stand-in
  const setDriverType = t => {
    setForm(f => ({
      ...f, driver_type: t,
      driver_name: t === "assigned" ? (selectedVehicle?.driver_name || "") : "",
    }))
  }

  const save = async () => {
    if (!form.vehicle_id) return toastError("Vehicle is required")
    try {
      setSaving(true)
      await saveFleetAllotment({ ...form, sites: form.sites.filter(x => x.site_name?.trim()) })
      toastSuccess("Allotment saved")
      setModal(false)
      await load()
    } catch (e) { toastError(e?.message || "Save failed") } finally { setSaving(false) }
  }
  const remove = async r => {
    if (!window.confirm(`Remove allotment for ${r.plate}?`)) return
    try { await removeFleetAllotment(r.id); toastSuccess("Removed"); await load() } catch (e) { toastError(e?.message) }
  }

  const vehLabel = v => `${(v.plate_code ? v.plate_code + "-" : "")}${v.plate_no}${v.name_of_vehicle ? ` · ${v.name_of_vehicle}` : ""}${v.seater ? ` (${v.seater})` : ""}`

  const formCarried = useMemo(() => form.sites.reduce((a, x) => a + (Number(x.qty) || 0), 0), [form.sites])

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Breadcrumbs title="Fleet" breadcrumbObject={[{ title: "Fleet", link: "/fleet-dashboard" }, { title: "Daily Allotment", link: "/fleet-allotment" }]} />
          <div className="d-flex align-items-center gap-2">
            <Input type="date" bsSize="sm" style={{ width: 160 }} value={date} onChange={e => setDate(e.target.value)} />
            <Button size="sm" color="primary" onClick={openAdd}><i className="bx bx-plus me-1" /> Allot Vehicle</Button>
          </div>
        </div>

        <Row className="g-2 mb-3">
          {[
            { label: "Allotted Vehicles", value: num(s.allotted) },
            { label: "Seats Carried", value: num(s.carried) },
            { label: "Total Capacity", value: num(s.capacity) },
            { label: "Availability", value: num(s.availability) },
            { label: "Avg Utilisation", value: s.avg_utilisation != null ? `${s.avg_utilisation}%` : "—" },
          ].map((c, i) => (
            <Col md={2} xs={6} key={i}>
              <div style={{ border: "1px solid #eef0f4", borderRadius: 10, padding: "12px 14px", background: "#fff" }}>
                <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>{c.label}</div>
                <div className="fw-bold" style={{ fontSize: 20 }}>{c.value}</div>
              </div>
            </Col>
          ))}
        </Row>

        <Card>
          <CardBody>
            <div style={{ overflowX: "auto" }}>
              <Table responsive borderless className="align-middle tnt-table mb-0" style={{ fontSize: 13, minWidth: 1050 }}>
                <thead>
                  <tr>
                    <th style={{ width: 45 }}>#</th>
                    <th style={{ width: 110 }}>Vehicle</th>
                    <th style={{ width: 110 }}>Source</th>
                    <th style={{ width: 140 }}>Driver</th>
                    <th>Sites / Projects</th>
                    <th className="text-end" style={{ width: 80 }}>Carried</th>
                    <th className="text-end" style={{ width: 80 }}>Capacity</th>
                    <th className="text-end" style={{ width: 90 }}>Utilisation</th>
                    <th style={{ width: 80 }}>Shift</th>
                    <th className="text-end" style={{ width: 90 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="10" className="text-center py-4"><Spinner size="sm" /> Loading…</td></tr>}
                  {!loading && !rows.length && <tr><td colSpan="10" className="text-center text-muted py-4">No allotments for {date}. Click “Allot Vehicle”.</td></tr>}
                  {!loading && rows.map((r, i) => (
                    <tr key={r.id}>
                      <td className="text-muted">{i + 1}</td>
                      <td className="fw-500">{r.plate}<div className="text-muted" style={{ fontSize: 11 }}>{r.vehicle_type}</div></td>
                      <td>{r.source || "—"}</td>
                      <td>
                        {r.driver_name || "—"}
                        {r.driver_type === "hired" && (
                          <span className="badge bg-warning-subtle text-warning ms-1" style={{ fontSize: 10 }}>Hired</span>
                        )}
                      </td>
                      <td>
                        {r.sites?.length ? r.sites.map((x, j) => (
                          <span key={j} className="me-2 text-nowrap">{x.site_name}{x.area_project ? ` – ${x.area_project}` : ""} <b>({x.qty})</b>{j < r.sites.length - 1 ? "," : ""}</span>
                        )) : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-end fw-500">{num(r.carried)}</td>
                      <td className="text-end">{num(r.capacity)}</td>
                      <td className="text-end fw-600" style={{ color: pctColor(r.carried_pct) }}>{r.carried_pct != null ? `${r.carried_pct}%` : "—"}</td>
                      <td className="text-capitalize">{r.shift}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" color="link" className="p-0 me-2" onClick={() => openEdit(r)}><i className="bx bx-edit" /></Button>
                        <Button size="sm" color="link" className="p-0 text-danger" onClick={() => remove(r)}><i className="bx bx-trash" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setModal(false)}>{form.id ? "Edit" : "New"} Allotment</ModalHeader>
        <ModalBody>
          <Row className="g-2">
            <Col md={3}><FormGroup><Label>Date</Label><Input type="date" bsSize="sm" value={form.date} onChange={e => set("date", e.target.value)} /></FormGroup></Col>
            <Col md={5}><FormGroup><Label>Vehicle<span className="text-danger">*</span></Label>
              <Input type="select" bsSize="sm" value={form.vehicle_id} onChange={e => pickVehicle(e.target.value)}>
                <option value="">Select vehicle</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{vehLabel(v)}</option>)}
              </Input>
              {selectedVehicle && (
                <div className="text-muted small mt-1">
                  Total seats: <b>{selectedVehicle.seater ? num(selectedVehicle.seater) : "—"}</b>
                </div>
              )}
            </FormGroup></Col>
            <Col md={4}><FormGroup><Label>Driver type</Label>
              <Input type="select" bsSize="sm" value={form.driver_type} onChange={e => setDriverType(e.target.value)}>
                <option value="assigned">Own Driver</option>
                <option value="hired">Hired</option>
              </Input></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Driver</Label>
              {form.driver_type === "assigned" ? (
                <Input type="select" bsSize="sm" value={form.driver_name} onChange={e => set("driver_name", e.target.value)}>
                  <option value="">Select driver</option>
                  {assignedDrivers.map(d => <option key={d} value={d}>{d}</option>)}
                  {form.driver_name && !assignedDrivers.includes(form.driver_name) && (
                    <option value={form.driver_name}>{form.driver_name}</option>
                  )}
                </Input>
              ) : (
                <Input bsSize="sm" placeholder="Hired driver name" value={form.driver_name} onChange={e => set("driver_name", e.target.value)} />
              )}
            </FormGroup></Col>
            <Col md={4}><FormGroup><Label>Source (camp)</Label>
              <Input type="select" bsSize="sm" value={form.source} onChange={e => set("source", e.target.value)}>
                <option value="">Select camp</option>
                {camps.map(c => <option key={c.id} value={c.name}>{c.name}{c.location ? ` – ${c.location}` : ""}</option>)}
                {form.source && !camps.some(c => c.name === form.source) && (
                  <option value={form.source}>{form.source}</option>
                )}
              </Input></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Shift</Label><Input type="select" bsSize="sm" value={form.shift} onChange={e => set("shift", e.target.value)}><option value="day">Day</option><option value="night">Night</option></Input></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Duty start</Label><Input bsSize="sm" placeholder="6:00 AM" value={form.duty_start} onChange={e => set("duty_start", e.target.value)} /></FormGroup></Col>
            <Col md={3}><FormGroup><Label>Duty off</Label><Input bsSize="sm" placeholder="6:00 PM" value={form.duty_off} onChange={e => set("duty_off", e.target.value)} /></FormGroup></Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
            <b>Sites / Projects</b>
            <div className="text-muted small">Carried: <b>{formCarried}</b></div>
          </div>
          {form.sites.map((st, i) => {
            const pool = st.project_id ? memberPool[`${st.project_id}|${form.source || ""}`] : null
            return (
              <div className="border rounded p-2 mb-2" key={i}>
                <Row className="g-2 align-items-center">
                  <Col md={5}>
                    <Input type="select" bsSize="sm" value={st.project_id || (st.site_name ? "legacy" : "")}
                      onChange={e => pickProject(i, e.target.value === "legacy" ? "" : e.target.value)}>
                      <option value="">Select project</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.code ? ` · ${p.code}` : ""}</option>
                      ))}
                      {/* legs captured before the project master keep the name that was typed */}
                      {!st.project_id && st.site_name && <option value="legacy">{st.site_name}</option>}
                    </Input>
                  </Col>
                  <Col md={4}><Input bsSize="sm" placeholder="Area / project" value={st.area_project} onChange={e => setSite(i, "area_project", e.target.value)} /></Col>
                  <Col md={2}><Input type="number" min="0" bsSize="sm" placeholder="Seats" value={st.qty} onChange={e => setSite(i, "qty", e.target.value)} /></Col>
                  <Col md={1}><Button size="sm" color="link" className="text-danger p-0" onClick={() => rmSite(i)} disabled={form.sites.length === 1}><i className="bx bx-x" /></Button></Col>
                </Row>

                {st.project_id && (
                  <div className="mt-2">
                    {pool?.loading && <div className="text-muted small">Loading members…</div>}
                    {pool?.error && <div className="text-danger small">Could not load members for this project.</div>}
                    {pool && !pool.loading && !pool.error && !pool.members.length && (
                      <div className="text-muted small">
                        No members assigned to this project{form.source ? ` staying at ${form.source}` : ""}.
                      </div>
                    )}
                    {pool && !pool.loading && !pool.error && !!pool.members.length && (
                      <>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small text-muted">
                            Members <b>{st.member_ids.length}</b> / {pool.members.length} selected
                            {form.source && !pool.camp_link_available && " · not narrowed by camp"}
                          </span>
                          <Button size="sm" color="link" className="p-0 small" onClick={() => toggleAllMembers(i, pool.members)}>
                            {st.member_ids.length === pool.members.length ? "Clear all" : "Select all"}
                          </Button>
                        </div>
                        <div className="border rounded p-2 mt-1" style={{ maxHeight: 160, overflowY: "auto" }}>
                          {pool.members.map(m => (
                            <div className="form-check" key={m.id}>
                              <Input className="form-check-input" type="checkbox" id={`site-${i}-mem-${m.id}`}
                                checked={st.member_ids.some(x => String(x) === String(m.id))}
                                onChange={() => toggleMember(i, m.id)} />
                              <Label className="form-check-label small" for={`site-${i}-mem-${m.id}`}>
                                {m.name}{m.code ? ` · ${m.code}` : ""}{m.camp ? ` · ${m.camp}` : ""}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          <Button size="sm" color="link" className="p-0" onClick={addSite}><i className="bx bx-plus" /> Add site</Button>

          <Row className="g-2 mt-1">
            <Col md={3}><FormGroup><Label>Division</Label><Input bsSize="sm" value={form.division} onChange={e => set("division", e.target.value)} /></FormGroup></Col>
            <Col md={4}><FormGroup><Label>Contact person</Label><Input bsSize="sm" value={form.contact_person} onChange={e => set("contact_person", e.target.value)} /></FormGroup></Col>
            <Col md={5}><FormGroup><Label>Remarks</Label><Input bsSize="sm" value={form.remarks} onChange={e => set("remarks", e.target.value)} /></FormGroup></Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="primary" size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default FleetAllotment
